/**
 * KPI Audit — admin-only page exposing every catalogued KPI, its query
 * source, live value, last-computed timestamp and validation status.
 *
 * Consumes GET /api/analytics/kpi-audit which delegates to
 * `analytics_service.compute_all()`.  Every value on this page is the
 * SAME value the dashboard and reports use — that's the whole point.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const CATEGORY_LABEL = {
  revenue:      "Revenue & Billing",
  patients:     "Patients",
  appointments: "Appointments",
  inventory:    "Inventory",
  whatsapp:     "WhatsApp",
};

export default function KPIAudit() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/kpi-audit");
      setData(data);
    } catch (e) {
      toast.error(apiError(e, "Could not load KPI audit"));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="kpi-audit-forbidden">
        <div className="overline text-ink-soft">Admin only</div>
        <h1 className="mt-2 font-display text-2xl">KPI Audit is restricted</h1>
        <p className="mt-2 text-sm text-ink-mid">Ask an admin to enable access.</p>
      </div>
    );
  }

  // Group by category client-side for rendering.
  const groups = {};
  (data?.kpis || []).forEach((k) => {
    (groups[k.category] = groups[k.category] || []).push(k);
  });

  const fmt = (row) => {
    if (row.value === null || row.value === undefined) return "—";
    if (typeof row.value === "object") {
      // For structured KPIs (revenue window, collections rate, no_show)
      if ("revenue"   in row.value) return `${row.unit}${Number(row.value.revenue).toLocaleString("en-IN")}`;
      if ("rate_pct"  in row.value) return `${row.value.rate_pct}${row.unit}`;
      return JSON.stringify(row.value);
    }
    if (row.unit === "₹") return `${row.unit}${Number(row.value).toLocaleString("en-IN")}`;
    if (row.unit === "%") return `${Number(row.value).toFixed(1)}${row.unit}`;
    return `${Number(row.value).toLocaleString("en-IN")}`;
  };

  return (
    <div className="space-y-6" data-testid="kpi-audit">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-ink-soft">Admin · analytics diagnostics</div>
          <h1 className="mt-2 font-display text-4xl tracking-tight">KPI Audit</h1>
          <p className="mt-2 text-sm text-ink-mid max-w-2xl">
            Every KPI Viyora displays anywhere — dashboard, analytics hub, doctor leaderboard, reports.
            Same query, same value, same timezone. If a number here is wrong, every other page is wrong the same way.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="kpi-audit-refresh"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E8EAE9] hover:border-pine text-sm px-4 py-2 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpi-audit-summary">
          <SummaryCard label="Total KPIs"    value={data.total_kpis} />
          <SummaryCard label="Passed"        value={data.passed}      color="pine" />
          <SummaryCard label="Failed"        value={data.failed}      color={data.failed > 0 ? "red" : "ink-soft"} />
          <SummaryCard label="Last computed" value={data.kpis?.[0]?.computed_at ? new Date(data.kpis[0].computed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—"} small />
        </div>
      )}

      {Object.entries(groups).map(([cat, rows]) => (
        <div key={cat} data-testid={`kpi-audit-group-${cat}`}>
          <div className="mb-2 text-[10px] uppercase tracking-widest text-ink-soft">{CATEGORY_LABEL[cat] || cat}</div>
          <div className="rounded-3xl border border-[#E8EAE9] overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-linen/40 text-[10px] uppercase tracking-widest text-ink-soft">
                <tr>
                  <th className="text-left px-4 py-2">KPI</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Source</th>
                  <th className="text-right px-4 py-2">Value</th>
                  <th className="text-center px-4 py-2 w-16">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-[#F0EBE0]" data-testid={`kpi-row-${row.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{row.label}</div>
                      <div className="text-[10px] text-ink-soft mt-0.5">{row.id}</div>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-[11px] text-ink-mid font-mono">{row.source}</td>
                    <td className="px-4 py-2.5 text-right font-display text-lg tabular-nums">{fmt(row)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {row.ok
                        ? <CheckCircle2 className="w-4 h-4 text-pine mx-auto" />
                        : <span title={row.error} className="inline-flex items-center gap-1 text-[#B94A48]"><AlertCircle className="w-4 h-4" /></span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {data?.note && (
        <div className="rounded-2xl border border-dashed border-[#E8EAE9] bg-linen/40 p-4 text-xs text-ink-mid">
          {data.note}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = "ink", small }) {
  const colorClass = {
    pine:      "text-pine",
    red:       "text-[#B94A48]",
    "ink-soft":"text-ink-soft",
    ink:       "",
  }[color] || "";
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`mt-1 font-display ${small ? "text-lg" : "text-3xl"} ${colorClass}`}>{value}</div>
    </div>
  );
}
